import { Table } from '@/components/Table';
import { WithLoader } from '@/components/WithLoader';
import { useModelContributionContext } from '@/dashboards/ModelContributions/hooks';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { cn } from '@/utils';
import { roundToDecimals } from '@/utils/numberUtils';
import { Tooltip, Typography } from '@analytical-alley/ui';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import React, { ReactNode, useMemo } from 'react';

export interface TableData {
    category: string;
    variable: string;
    contribution: number;
    percentOfTotalContribution: number;
    contributionPercentTotal: number;
}

const columnHelper = createColumnHelper<TableData>();

interface VariableData {
    category: string;
    variable: string;
    contribution: number;
    percentOfTotalContribution: number;
    contributionPercentTotal: number;
}

const CellRender = <T extends VariableData, E extends ReactNode>({
    getValue,
    row,
    column: { id },
    table,
    className,
    children,
    disabled,
}: CellContext<T, E> & {
    className?: string;
    children?: ReactNode;
    disabled?: boolean;
}) => {
    const header = table.getColumn(id)?.columnDef.header;

    if (disabled) {
        return (
            <span className={cn('flex px-3.5 py-2', className)}>
                {children || getValue()}
            </span>
        );
    }

    return (
        <>
            <Tooltip placement="bottom">
                <Tooltip.Trigger disabled asChild>
                    <span
                        className={cn('flex px-3.5 py-2', className, {
                            'cursor-pointer': !disabled,
                        })}
                    >
                        {children || getValue()}
                    </span>
                </Tooltip.Trigger>
                <Tooltip.Content>
                    <div className="tooltip-content">
                        <table>
                            <tbody>
                                <tr>
                                    <td className="pr-2">
                                        <Typography className="text-black dark:text-black">
                                            Category:
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography className="text-dark dark:text-dark">
                                            {row.original.category}
                                        </Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="pr-2">
                                        <Typography className="text-black dark:text-black">
                                            Variable:
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography className="text-dark dark:text-dark">
                                            {row.original.variable}
                                        </Typography>
                                    </td>
                                </tr>
                                <tr>
                                    <td className="pr-2">
                                        <Typography className="text-black dark:text-black">
                                            {typeof header === 'function'
                                                ? header({} as never)
                                                : header}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography className="text-dark dark:text-dark">
                                            {children || getValue()}
                                        </Typography>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Tooltip.Content>
            </Tooltip>
        </>
    );
};

export const ContributionsTable = () => {
    const { contributionFormatter } = useDashboardGlobalContext();

    const {
        queryResult: { isLoading, isFetching },
        contributionData,
        selected,
        handleSelectedChange,
        selectedFilterNodes,
    } = useModelContributionContext();

    const columns = useMemo(
        () => [
            columnHelper.accessor('category', {
                header: () => 'Category',
                cell: (info) => {
                    return <CellRender {...info} disabled />;
                },
                footer: () => 'Total',
                meta: {
                    header: {
                        className: 'w-px',
                    },
                    footer: {
                        className: 'text-right',
                    },
                },
            }),
            columnHelper.accessor('variable', {
                cell: (info) => {
                    return <CellRender {...info} disabled />;
                },
                header: () => <span>Variable</span>,
            }),
            columnHelper.accessor('contribution', {
                header: () => (
                    <Typography tooltip="KPI generated by this variable over the whole modelling period">
                        Contribution
                    </Typography>
                ),
                meta: {
                    header: {
                        className: 'w-px',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                cell: (info) => {
                    return (
                        <CellRender {...info} className="justify-center">
                            {contributionFormatter(info.getValue())}
                        </CellRender>
                    );
                },
                footer: (info) => {
                    const totalContribution = info.table
                        .getRowModel()
                        .rows.reduce(
                            (acc, row) => acc + row.original.contribution,
                            0,
                        );
                    return (
                        <Typography
                            tooltip="Total KPI for the selected period."
                            className="text-center"
                        >
                            {contributionFormatter(totalContribution)}
                        </Typography>
                    );
                },
            }),
            columnHelper.accessor('percentOfTotalContribution', {
                header: () => (
                    <Typography tooltip="What portion of KPI contibuted by selected variables/categories was generated by this variable over the selected period.">
                        % of Filtered Contribution
                    </Typography>
                ),
                cell: (info) => {
                    return (
                        <CellRender {...info} className="justify-center">
                            {info.getValue().toFixed(2)}%
                        </CellRender>
                    );
                },
                meta: {
                    header: {
                        className: 'w-px',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                footer: (info) => {
                    const totalContribution = info.table
                        .getRowModel()
                        .rows.reduce(
                            (acc, row) => acc + row.original.contribution,
                            0,
                        );

                    return (
                        <Typography
                            tooltip="Total share of KPI contributions generated by all the variables."
                            className="text-center"
                        >
                            {info.table
                                .getRowModel()
                                .rows.reduce(
                                    (acc, row) =>
                                        acc +
                                        (row.original.contribution /
                                            totalContribution) *
                                            100,
                                    0,
                                )
                                .toFixed(2)}
                            %
                        </Typography>
                    );
                },
            }),
            columnHelper.accessor('contributionPercentTotal', {
                header: () => (
                    <Typography tooltip="What portion of KPI was generated by this variable over the selected period.">
                        % of Total Contribution
                    </Typography>
                ),
                cell: (info) => {
                    return (
                        <CellRender {...info} className="justify-center">
                            {info.getValue().toFixed(2)}%
                        </CellRender>
                    );
                },
                meta: {
                    header: {
                        className: 'w-px',
                    },
                    cell: {
                        className: 'text-center',
                    },
                    footer: {
                        className: 'text-center',
                    },
                },
                footer: (info) => {
                    return (
                        <Typography
                            className="text-center"
                            tooltip="Total share of KPI contributions generated by all the selected variables."
                        >
                            {info.table
                                .getRowModel()
                                .rows.reduce(
                                    (acc, row) =>
                                        acc +
                                        row.original.contributionPercentTotal,
                                    0,
                                )
                                .toFixed(2)}
                            %
                        </Typography>
                    );
                },
            }),
        ],
        [contributionFormatter],
    );

    const contributionForAllSelectedVariables = useMemo(
        () =>
            contributionData.variableData.reduce((acc, row) => {
                if (
                    !!(
                        selectedFilterNodes[row.category] ||
                        selectedFilterNodes[row.displayName]
                    )
                ) {
                    acc += contributionData.totalContributionByVariable.get(
                        row.displayName,
                    )!;
                }

                return acc;
            }, 0),
        [
            contributionData.totalContributionByVariable,
            contributionData.variableData,
            selectedFilterNodes,
        ],
    );

    const tableData = useMemo(
        () =>
            contributionData.variableData
                .filter((d) => {
                    return !!(
                        selectedFilterNodes[d.category] ||
                        selectedFilterNodes[d.displayName]
                    );
                })
                .map((data) => ({
                    category: data.category,
                    variable: data.displayName,
                    contribution:
                        contributionData.totalContributionByVariable.get(
                            data.displayName,
                        )!,
                    percentOfTotalContribution: Number(
                        (contributionData.totalContributionByVariable.get(
                            data.displayName,
                        )! /
                            contributionForAllSelectedVariables) *
                            100,
                    ),
                    contributionPercentTotal: Number(
                        (contributionData.totalContributionByVariable.get(
                            data.displayName,
                        )! /
                            contributionData.totalContribution) *
                            100,
                    ),
                }))
                .filter((d) => roundToDecimals(d.contribution) !== 0)
                .sort((a, b) => {
                    const totalContributionA =
                        contributionData.totalContributionByCategory.get(
                            a.category,
                        )!;
                    const totalContributionB =
                        contributionData.totalContributionByCategory.get(
                            b.category,
                        )!;
                    if (totalContributionA < totalContributionB) {
                        return 1;
                    }
                    if (totalContributionA > totalContributionB) {
                        return -1;
                    }

                    return b.contribution - a.contribution;
                }),
        [
            contributionData.totalContribution,
            contributionData.totalContributionByCategory,
            contributionData.totalContributionByVariable,
            contributionData.variableData,
            contributionForAllSelectedVariables,
            selectedFilterNodes,
        ],
    );

    const isActiveRow = (row: TableData) => row.variable === selected;

    const onRowClick = (row: TableData) => {
        handleSelectedChange(selected === row.variable ? '' : row.variable);
    };

    return (
        <div className="glass-tile p-6">
            <div className="flex mb-4">
                <Typography
                    variant="titleS"
                    tooltip="Focus on the specific key drivers of the business results over the selected period."
                >
                    Model contribution by variable
                </Typography>
            </div>
            <WithLoader
                className={isLoading ? 'min-h-96' : undefined}
                isLoading={isLoading}
                isFetching={isFetching}
            >
                <Table
                    isLoading={isLoading}
                    data={tableData}
                    columns={columns}
                    shouldSpanRow
                    onRowClick={onRowClick}
                    isActiveRow={isActiveRow}
                    colSpan={{ footer: { value: 2, index: 0 } }}
                />
            </WithLoader>
        </div>
    );
};
